import { FadeIn, FadeInStagger, StaggerContainer, ScaleIn } from "../components/AnimationWrapper";
import { trackEvent } from "../analytics/trackEvent.js";
import PageMeta from "../components/PageMeta";
import NavbarUA from "../components/NavbarUA";
import FooterUA from "../components/FooterUA";
import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { StarIcon } from "@tailgrids/icons";

const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.75024 13.501C6.33613 13.501 6.0004 13.1651 6.00024 12.751V7.50024H0.75C0.335786 7.50024 0 7.16445 0 6.75024C0 6.33603 0.335786 6.00024 0.75 6.00024H6.00024V0.75C6.00024 0.335786 6.33603 0 6.75024 0C7.16445 0 7.50024 0.335786 7.50024 0.75V6.00024H12.751C13.1651 6.0004 13.501 6.33613 13.501 6.75024C13.501 7.16435 13.1651 7.50008 12.751 7.50024H7.50024V12.751C7.50008 13.1651 7.16435 13.501 6.75024 13.501Z"
      fill="#374151"
    />
  </svg>
);

const MinusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M6 11.2498C5.58579 11.2498 5.25 11.5855 5.25 11.9998C5.25 12.414 5.58579 12.7498 6 12.7498V11.9998V11.2498ZM18.0007 12.7498C18.4149 12.7498 18.7507 12.414 18.7507 11.9998C18.7507 11.5855 18.4149 11.2498 18.0007 11.2498V11.9998V12.7498ZM6 11.9998V12.7498H18.0007V11.9998V11.2498H6V11.9998Z"
      fill="#374151"
    />
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" className="inline-block">
    <path d="M4 10l4 4 8-8" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CrossIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" className="inline-block">
    <path d="M6 6l8 8M14 6l-8 8" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export default function HomeUA() {
  const [openItem, setOpenItem] = useState(null);
  const heroHeadingRef = useRef(null);

  const toggleItem = (id) => setOpenItem(openItem === id ? null : id);

  const handleCTA = (location) => {
    trackEvent("click_register_button", { ab: "UA-HERO", location });
    trackEvent("conversion", { send_to: "AW-18114124186/1vd0CLTGk6EcEJqzvr1D" });
    window.location.href = "https://app.cv-box.com/#signup";
  };

  // ─── FAQ Data ───
  const faqItems = [
    {
      id: 1,
      question: "Що я можу зробити на безкоштовному тарифі?",
      answer:
        "Побудувати профіль, провести ATS-перевірку, згенерувати базове CV, відстежувати вакансії, та отримати повний доступ до 1 вакансії (звіт сумісності, CV, cover letter, 5 питань Job Agent, підготовка до співбесіди).",
    },
    {
      id: 2,
      question: "Як працює Job Agent?",
      answer:
        "Job Agent - це твій AI кар'єрний коуч. Задавай питання про вакансію, отримуй допомогу з підготовки до співбесіди або вдосконалюй стратегію подання заявки. Доступний на тарифах Silver та Gold.",
    },
    {
      id: 3,
      question: "Чи безпечні мої дані?",
      answer:
        "Так. Твоє CV та дані профілю зашифровані та зберігаються безпечно. Ми ніколи не ділимось твоєю інформацією з третіми сторонами. Ти можеш видалити свої дані в будь-який час.",
    },
    {
      id: 4,
      question: "Чи можу я використовувати CV Box для будь-якого сайту вакансій?",
      answer:
        "Безумовно. Працює з LinkedIn, Indeed, Glassdoor, DOU, Djinni, Work.ua та будь-яким іншим сайтом, де можна вставити URL вакансії.",
    },
    {
      id: 5,
      question: "Що відбувається після скасування підписки?",
      answer:
        "Раніше відкриті вакансії залишаються доступними. Нові вакансії можна відкривати за €0.99 кожна. Безкоштовні функції (ATS-перевірка, базове CV, трекінг) працюють завжди.",
    },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };

  // ─── Pain Points Data ───
  const painPoints = [
    {
      emoji: "😔",
      title: "Тижні мовчання",
      description:
        "Відгук відправлено - і більше нічого. Незрозуміло, чи варто чекати, чи вакансія вже закрита, чи просто твоє CV не дійшло до потрібних очей.",
    },
    {
      emoji: "🔄",
      title: "Одне й те саме CV, знову і знову",
      description:
        "Кожна вакансія вимагає трохи іншого акценту, але переписувати резюме вручну щоразу - це години, яких і так бракує під час активного пошуку.",
    },
    {
      emoji: "❓",
      title: "Незрозумілі критерії відбору",
      description:
        "Ти ніколи не дізнаєшся, чому саме тебе не покликали - не вистачило досвіду, невдалі формулювання, чи CV просто не пройшло автоматичний відбір.",
    },
    {
      emoji: "😩",
      title: "Втома від самого процесу",
      description:
        "Пошук роботи перетворюється на другу роботу - без зарплати, дедлайнів і зрозумілого результату наприкінці дня.",
    },
    {
      emoji: "📉",
      title: "Падіння впевненості з кожним днем тиші",
      description:
        "Що довше немає відповіді, то важче вірити у власну цінність як кандидата - хоча проблема часто зовсім не в тобі, а в тому, як подано інформацію.",
    },
  ];

  // ─── Steps Data ───
  const steps = [
    {
      image: "/images/features/step-1.webp",
      title: "1. Побудуй профіль",
      description:
        "Завантаж своє CV і дозволь AI розібрати твій професійний досвід. Перевір ATS-сумісність та згенеруй базове CV - все безкоштовно.",
    },
    {
      image: "/images/features/step-2.webp",
      title: "2. Відстежуй вакансії",
      description:
        "Додавай вакансії за URL. Відстежуй статус кожної заявки (Нова → В роботі → Відправлена) у персональному Job Space.",
    },
    {
      image: "/images/features/step-3.webp",
      title: "3. Аналізуй сумісність",
      description:
        "Отримай детальний звіт сумісності, що порівнює твій профіль із конкретною вакансією - сильні сторони, прогалини та рекомендації.",
    },
    {
      image: "/images/features/step-4.webp",
      title: "4. Генеруй матеріали під вакансію",
      description:
        "Створюй CV та cover letter, оптимізовані під кожну вакансію. Редагуй та зберігай необмежену кількість версій.",
    },
    {
      image: "/images/features/step-5.webp",
      title: "5. Отримай AI-коучинг",
      description:
        "Спілкуйся з Job Agent про конкретні вакансії - отримуй допомогу з підготовки до співбесіди, переговорів про зарплату та стратегії подання.",
    },
  ];

  // ─── Platform Features Data ───
  const platformFeatures = [
    {
      emoji: "🌍",
      title: "Англомовні вакансії - без втрати сенсу",
      description:
        "CV та cover letter генеруються з урахуванням стандартів, прийнятих у міжнародних компаніях - структура, обсяг, тон, яких очікує іноземний рекрутер.",
    },
    {
      emoji: "⚙️",
      title: "Розуміння технічного стеку",
      description:
        "AI розпізнає твої навички та технології (мови програмування, фреймворки, інструменти) і правильно відображає їх у форматі, зрозумілому для technical recruiter та hiring manager.",
    },
    {
      emoji: "🏢",
      title: "Адаптація під тип компанії",
      description:
        "Продуктова компанія, аутсорс чи стартап - кожен формат очікує іншого акценту в CV. CV Box підлаштовує подачу досвіду під конкретний тип роботодавця.",
    },
  ];

  // ─── Pipeline Steps Data ───
  const pipelineSteps = [
    { number: 1, title: "📄 Завантаж профіль", description: "Завантаж своє CV у форматі PDF або заповни профіль вручну - AI розбере твій професійний досвід за секунди" },
    { number: 2, title: "🔍 ATS-перевірка", description: "Дізнайся, чи пройде твоє CV автоматичний відбір у Western ATS системах (Greenhouse, Lever, Workday)" },
    { number: 3, title: "✅ Затверди базове CV", description: "Переглянь згенероване ATS-friendly CV у markdown-редакторі та затверди як основу для всіх майбутніх заявок" },
    { number: 4, title: "🎯 Додай вакансію", description: "Вставь URL вакансії - система автоматично витягне вимоги, ключові слова, навички та очікування роботодавця" },
    { number: 5, title: "📊 Звіт сумісності", description: "Отримай детальний аналіз: твої сильні сторони, прогалини у досвіді та конкретні рекомендації що додати" },
    { number: 6, title: "✍️ CV + Cover Letter", description: "Згенеруй CV та супровідний лист, оптимізовані під ключові слова та вимоги конкретної вакансії" },
    { number: 7, title: "🎤 Підготовка до інтерв'ю", description: "Отримай персоналізований гайд для будь-якого етапу - HR-дзвінок, технічне інтерв'ю чи зустріч з менеджером. Будь готовий на кожному кроці" },
  ];

  // ─── Pricing Data ───
  const plans = [
    {
      name: "Free",
      price: "Free",
      period: null,
      description: "Ідеально для старту",
      badge: null,
      features: [
        "1 вакансія з повним доступом",
        "1 звіт сумісності",
        "1 оптимізоване CV",
        "1 cover letter",
        "5 питань Job Agent",
        "1 підготовка до співбесіди (кожен тип)",
      ],
      cta: "Почати безкоштовно",
      highlighted: false,
    },
    {
      name: "Bronze",
      price: "€9.99",
      period: "/міс",
      description: "~10 вакансій на місяць",
      badge: null,
      features: [
        "10 вакансій з повним доступом",
        "10 звітів сумісності",
        "10 оптимізованих CV",
        "10 cover letters",
        { text: "Job Agent - недоступний", unavailable: true },
        { text: "Підготовка до співбесід - недоступна", unavailable: true },
      ],
      cta: "Обрати BRONZE",
      highlighted: false,
    },
    {
      name: "Silver",
      price: "€19.99",
      period: "/міс",
      description: "~25 вакансій на місяць",
      badge: "Популярний",
      features: [
        "25 вакансій з повним доступом",
        "25 звітів сумісності",
        "Необмежені CV",
        "Необмежені cover letters",
        "125 питань Job Agent",
        "25 підготовок до HR-співбесід",
      ],
      cta: "Обрати SILVER",
      highlighted: true,
    },
    {
      name: "Gold",
      price: "€39.99",
      period: "/міс",
      description: "Необмежений доступ",
      badge: "Найкращий",
      features: [
        "Необмежені вакансії",
        "Необмежені звіти сумісності",
        "Необмежені CV",
        "Необмежені cover letters",
        "Необмежений Job Agent",
        "Всі типи підготовки до співбесід",
      ],
      cta: "Обрати GOLD",
      highlighted: false,
    },
  ];

  // ─── Testimonials Data ───
  const testimonials = [
    {
      name: "Олексій Шевченко",
      role: "Senior Frontend Developer",
      location: "Берлін, Німеччина",
      quote: "Після 40+ відмов без пояснень, CV Box показав що мій CV не проходив ATS. Переробив - за 2 тижні отримав 5 запрошень на інтерв'ю.",
      image: "/images/testimonial/avatar-1.webp",
    },
    {
      name: "Марина Коваленко",
      role: "DevOps Engineer",
      location: "Амстердам, Нідерланди",
      quote: "Не розуміла, чого очікують нідерландські компанії. Compatibility report показав прогалини. Через місяць - офер від фінтех-стартапу.",
      image: "/images/testimonial/avatar-2.webp",
    },
    {
      name: "Дмитро Бондаренко",
      role: "Backend Engineer (Java)",
      location: "Дублін, Ірландія",
      quote: "Job Agent допоміг підготуватись до behavioral інтерв'ю - знав які приклади підходять під конкретну вакансію. Отримав Senior позицію.",
      image: "/images/testimonial/avatar-3.webp",
    },
    {
      name: "Катерина Литвин",
      role: "QA Lead",
      location: "Стокгольм, Швеція",
      quote: "Cover letter генерувався за хвилину замість години. Головне - він відображав мій досвід у контексті конкретної вакансії.",
      image: "/images/testimonial/avatar-4.webp",
    },
    {
      name: "Артем Мельник",
      role: "Data Engineer",
      location: "Варшава, Польща",
      quote: "Адаптація CV під кожну вакансію забирала вечір. Тепер - 5 хвилин. За 3 місяці подав 20+ заявок і отримав 3 офери.",
      image: "/images/testimonial/avatar-5.webp",
    },
  ];

  return (
    <>
      <NavbarUA />
      <PageMeta
        title="CV Box – Оптимізація CV для EU remote вакансій | ATS-сумісність для українських IT-спеціалістів"
        description="Перетвори українське резюме у формат, який проходить Western ATS. Генеруй CV та cover letter під кожну EU-вакансію. Безкоштовна ATS-перевірка."
      />
      <Helmet>
        <html lang="uk" />
        <link rel="canonical" href="https://cv-box.com/ua" />
        <link rel="alternate" href="https://cv-box.com/ua" hrefLang="uk" />
        <link rel="alternate" href="https://cv-box.com" hrefLang="en" />
        <link rel="alternate" href="https://cv-box.com" hrefLang="x-default" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta name="geo.region" content="UA" />
        <meta name="geo.placename" content="Ukraine" />
        <meta name="language" content="uk" />
        <meta property="og:site_name" content="CV Box" />
        <meta property="og:title" content="CV Box – Оптимізація CV для EU remote вакансій" />
        <meta property="og:description" content="Перетвори українське резюме у формат, який проходить Western ATS. Генеруй CV та cover letter під кожну EU-вакансію." />
        <meta property="og:image" content="https://cv-box.com/images/og/cv-box-ua.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content="https://cv-box.com/ua" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="uk_UA" />
        <meta property="og:locale:alternate" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="CV Box – Оптимізація CV для EU remote вакансій" />
        <meta name="twitter:description" content="AI-платформа для українських IT-спеціалістів. ATS-перевірка, генерація CV та cover letter під кожну вакансію." />
        <meta name="twitter:image" content="https://cv-box.com/images/og/cv-box-ua.png" />
        <meta name="keywords" content="резюме, CV, ATS, оптимізація резюме, пошук роботи, remote робота, EU вакансії, cover letter, AI резюме, українське резюме, робота за кордоном, IT вакансії" />
      </Helmet>

      {/* Structured Data - SoftwareApplication */}
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "CV Box",
          "url": "https://cv-box.com/ua",
          "description": "AI-платформа для оптимізації CV під EU remote вакансії. ATS-перевірка, генерація CV та cover letter під кожну вакансію, звіт сумісності та підготовка до інтерв'ю.",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "inLanguage": "uk",
          "offers": [
            { "@type": "Offer", "name": "Free", "price": "0", "priceCurrency": "EUR", "description": "1 вакансія з повним доступом" },
            { "@type": "Offer", "name": "Bronze", "price": "9.99", "priceCurrency": "EUR", "description": "10 вакансій на місяць" },
            { "@type": "Offer", "name": "Silver", "price": "19.99", "priceCurrency": "EUR", "description": "25 вакансій на місяць" },
            { "@type": "Offer", "name": "Gold", "price": "39.99", "priceCurrency": "EUR", "description": "Необмежений доступ" }
          ],
          "featureList": [
            "ATS-оптимізація CV",
            "Генерація CV під конкретну вакансію",
            "Cover letter генератор",
            "Звіт сумісності з вакансією",
            "AI кар'єрний коучинг (Job Agent)",
            "Трекінг вакансій",
            "Підготовка до інтерв'ю"
          ],
          "audience": { "@type": "Audience", "audienceType": "IT professionals seeking EU remote positions" }
        })}</script>
      </Helmet>

      <main>
        {/* ═══════════════════════════════════════════════════════════════════
            BLOCK 1: Hero
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] relative overflow-hidden min-h-screen flex items-center">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[radial-gradient(ellipse_at_center,rgba(99,102,241,0.12)_0%,transparent_70%)] pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[radial-gradient(ellipse_at_center,rgba(52,211,153,0.08)_0%,transparent_70%)] pointer-events-none"></div>

          <div className="max-w-7xl mx-auto px-6 w-full pt-32 pb-16 lg:pt-36 lg:pb-0">
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-8">
              {/* LEFT - Text */}
              <div className="lg:w-5/12 text-center lg:text-left">
                <FadeInStagger>
                  <FadeIn delay={0.1}>
                    <h1 ref={heroHeadingRef} className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6">
                      CV Box - твій персональний{" "}
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-green-400">кар&#39;єрний асистент</span>
                    </h1>
                  </FadeIn>

                  <FadeIn delay={0.2}>
                    <p className="text-white/60 text-lg lg:text-xl max-w-lg mb-6 leading-relaxed">
                      Будуй профіль один раз, а CV, cover letter і підготовку до співбесіди отримуй під кожну вакансію за лічені хвилини
                    </p>
                  </FadeIn>

                  <FadeIn delay={0.25}>
                    <p className="text-green-400 text-sm font-medium mb-4">Сервіс №1 для створення CV в Україні</p>
                    <ul className="space-y-2.5 mb-8 text-left max-w-lg mx-auto lg:mx-0">
                      {[
                        "Один профіль - необмежена кількість персоналізованих CV",
                        "Автоматичний трекінг усіх заявок – від відгуку до офера",
                        "AI-аналіз сумісності з вакансією до того, як ти відправиш відгук",
                        "Персональний AI-коуч для підготовки до співбесіди",
                        "Історія версій - жодна заявка не губиться і не переписується заново",
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-white text-sm">
                          <span className="w-5 h-5 rounded-full bg-green-500 inline-flex items-center justify-center shrink-0 mt-0.5">
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 10 10" fill="none">
                              <path d="M8.37605 2.7251L3.82605 7.2751L1.62396 5.07304" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </FadeIn>

                  <FadeIn delay={0.3}>
                    <div className="flex gap-4 items-center flex-col sm:flex-row lg:justify-start justify-center">
                      <button
                        onClick={() => handleCTA("hero")}
                        className="cursor-pointer rounded-full font-bold text-base py-3.5 px-8 bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-400 hover:to-emerald-500 hover:shadow-lg hover:shadow-green-500/30 transition-all duration-300"
                      >
                        Створи перше CV безкоштовно
                      </button>
                    </div>
                    <p className="text-white/50 text-sm mt-4 text-center lg:text-left">
                      ⭐ 4.97/5 - інструмент, яким вже користуються сотні IT-фахівців для пошуку роботи
                    </p>
                  </FadeIn>
                </FadeInStagger>
              </div>

              {/* RIGHT - Product image */}
              <FadeIn delay={0.3} className="lg:w-7/12 lg:-mr-16 xl:-mr-24">
                <div className="relative">
                  <div className="p-2 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm shadow-2xl shadow-indigo-500/10">
                    <img
                      src="/images/covers/cv-hero-image.png"
                      className="w-full rounded-xl"
                      alt="CV Box - побудова профілю, відстеження вакансій та AI кар'єрні інструменти"
                      fetchPriority="high"
                      width={1401}
                      height={747}
                    />
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            BLOCK 2: Pain Points
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <FadeIn>
              <h2 className="text-4xl lg:text-5xl font-medium text-gray-900 text-center mb-16 -tracking-[1.9px]">
                Найгірше в пошуку роботи - не відмова, а тиша
              </h2>
            </FadeIn>

            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {painPoints.map((card, index) => (
                <FadeIn key={index} className="bg-white rounded-2xl p-6 shadow-feature-card">
                  <span className="text-4xl mb-4 block">{card.emoji}</span>
                  <h3 className="text-2xl font-medium text-gray-900 mb-2">{card.title}</h3>
                  <p className="text-base text-gray-500">{card.description}</p>
                </FadeIn>
              ))}
            </StaggerContainer>

            <div className="text-center mt-12">
              <button
                onClick={() => handleCTA("pain-points")}
                className="cursor-pointer rounded-full font-bold text-base py-3.5 px-8 bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-400 hover:to-emerald-500 hover:shadow-lg hover:shadow-green-500/30 transition-all duration-300"
              >
                Створи CV безкоштовно
              </button>
              <p className="text-gray-500 text-sm mt-4">
                CV Box вирішує кожну з цих проблем - за п&#39;ять простих кроків 👇
              </p>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            BLOCK 3: Steps
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="bg-gray-50 py-28">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="max-w-2xl mx-auto text-center mb-16">
              <FadeIn>
                <h2 className="text-3xl lg:text-5xl -tracking-[1.9px] mb-4 font-medium text-gray-900">
                  5 кроків до твого успішного CV
                </h2>
              </FadeIn>
              <FadeIn delay={0.1}>
                <p className="text-lg text-gray-500">
                  П&#39;ять простих кроків, щоб перетворити будь-яку вакансію на персоналізовану заявку, яка пройде автоматичний відбір.
                </p>
              </FadeIn>
            </div>

            <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {steps.map((step, index) => (
                <FadeIn key={index} className="bg-white rounded-2xl p-3 shadow-feature-card">
                  <img src={step.image} className="rounded-xl w-full" alt={step.title} loading="lazy" width={741} height={648} />
                  <div className="p-5">
                    <h3 className="text-xl font-medium text-gray-900 mb-2">{step.title}</h3>
                    <p className="text-base text-gray-500">{step.description}</p>
                  </div>
                </FadeIn>
              ))}
            </StaggerContainer>

            <div className="text-center mt-12">
              <button
                onClick={() => handleCTA("steps")}
                className="cursor-pointer rounded-full font-bold text-base py-3.5 px-8 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white hover:from-indigo-400 hover:to-indigo-500 hover:shadow-lg hover:shadow-indigo-500/30 transition-all duration-300"
              >
                СПРОБУВАТИ БЕЗКОШТОВНО
              </button>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            Competitor Grid
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="max-w-2xl mx-auto text-center mb-16">
              <FadeIn>
                <h2 className="text-3xl lg:text-5xl -tracking-[1.9px] mb-4 font-medium text-gray-900">
                  Чому CV Box, а не інші?
                </h2>
              </FadeIn>
              <FadeIn delay={0.1}>
                <p className="text-lg text-gray-500">
                  Порівняй можливості - і побач різницю
                </p>
              </FadeIn>
            </div>

            <FadeIn>
              <div className="bg-white rounded-2xl shadow-feature-card overflow-x-auto">
                <table className="w-full min-w-[700px]">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="py-5 px-6 text-left text-sm font-medium text-gray-500">Функція</th>
                      <th className="py-5 px-4 text-center">
                        <div className="text-sm font-bold text-indigo-600">CV Box</div>
                        <div className="text-xs text-indigo-400 mt-0.5">✨ all-in-one</div>
                      </th>
                      <th className="py-5 px-4 text-center text-sm font-medium text-gray-500">robota.ua</th>
                      <th className="py-5 px-4 text-center text-sm font-medium text-gray-500">work.ua</th>
                      <th className="py-5 px-4 text-center text-sm font-medium text-gray-500">DOU</th>
                      <th className="py-5 px-4 text-center text-sm font-medium text-gray-500">Djinni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { feature: "ATS-оптимізація", values: [true, false, false, false, false] },
                      { feature: "CV під кожну вакансію", values: [true, false, false, false, false] },
                      { feature: "Cover letter", values: [true, false, false, false, false] },
                      { feature: "Скоринг сумісності", values: [true, false, false, false, false] },
                      { feature: "Трекінг вакансій", values: [true, true, true, false, false] },
                      { feature: "AI-коучинг", values: [true, false, false, false, false] },
                      { feature: "Підготовка до співбесіди", values: [true, false, false, false, false] },
                    ].map((row, i) => (
                      <tr key={i} className="border-b border-gray-50 last:border-0">
                        <td className="py-4 px-6 text-sm font-medium text-gray-900">{row.feature}</td>
                        {row.values.map((val, j) => (
                          <td key={j} className={`py-4 px-4 text-center ${j === 0 ? "bg-indigo-50/30" : ""}`}>
                            {val ? <CheckIcon /> : <CrossIcon />}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </FadeIn>

            <div className="text-center mt-12">
              <button
                onClick={() => handleCTA("competitor-grid")}
                className="cursor-pointer rounded-full font-bold text-base py-3.5 px-8 bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-400 hover:to-emerald-500 hover:shadow-lg hover:shadow-green-500/30 transition-all duration-300"
              >
                Спробувати CV Box безкоштовно
              </button>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            BLOCK 5: Platform Features
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="max-w-2xl mx-auto text-center mb-16">
              <FadeIn>
                <h2 className="text-4xl lg:text-5xl -tracking-[1.9px] mb-4 font-medium text-gray-900">
                  CV-BOX це платформа яка розуміє IT-ринок
                </h2>
              </FadeIn>
              <FadeIn delay={0.1}>
                <p className="text-lg text-gray-500">
                  Ми знаємо, що пошук роботи в IT - це не про шаблонні резюме, а про точність деталей!
                </p>
              </FadeIn>
            </div>

            <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {platformFeatures.map((feature, index) => (
                <FadeIn key={index} className="bg-white rounded-2xl p-6 shadow-feature-card">
                  <span className="text-4xl mb-4 block">{feature.emoji}</span>
                  <h3 className="text-2xl font-medium text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-base text-gray-500">{feature.description}</p>
                </FadeIn>
              ))}
            </StaggerContainer>

            <div className="text-center mt-12">
              <button
                onClick={() => handleCTA("platform-features")}
                className="cursor-pointer rounded-full font-bold text-base py-3.5 px-8 bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-400 hover:to-emerald-500 hover:shadow-lg hover:shadow-green-500/30 transition-all duration-300"
              >
                РОЗПОЧАТИ БЕЗКОШТОВНО
              </button>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            BLOCK 4 (HIDDEN): Pipeline - "Як CV Box оптимізує під кожну вакансію"
        ═══════════════════════════════════════════════════════════════════ */}
        {/*
        <section id="features" className="py-20 lg:py-28 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="max-w-2xl mx-auto text-center mb-16">
              <FadeIn>
                <h2 className="text-3xl lg:text-5xl -tracking-[1.9px] mb-4 font-medium text-gray-900">
                  Як CV Box оптимізує під кожну вакансію
                </h2>
              </FadeIn>
              <FadeIn delay={0.1}>
                <p className="text-lg text-gray-500">
                  Від одного профілю - до персоналізованих матеріалів для кожної позиції
                </p>
              </FadeIn>
            </div>

            <div className="max-w-2xl mx-auto">
              <StaggerContainer className="flex flex-col">
                {pipelineSteps.map((step, i) => (
                  <FadeIn key={i}>
                    <div className="flex gap-5 items-start">
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 rounded-full bg-indigo-500 text-white flex items-center justify-center text-lg font-bold shrink-0">
                          {step.number}
                        </div>
                        {i < pipelineSteps.length - 1 && (
                          <div className="w-0.5 h-16 bg-indigo-200 mt-2"></div>
                        )}
                      </div>
                      <div className="pb-8">
                        <h3 className="text-xl font-semibold text-gray-900 mb-1">{step.title}</h3>
                        <p className="text-base text-gray-600 leading-relaxed">{step.description}</p>
                      </div>
                    </div>
                  </FadeIn>
                ))}
              </StaggerContainer>
            </div>

            <div className="text-center mt-12">
              <button
                onClick={() => handleCTA("pipeline")}
                className="cursor-pointer rounded-full font-bold text-base py-3.5 px-8 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white hover:from-indigo-400 hover:to-indigo-500 hover:shadow-lg hover:shadow-indigo-500/30 transition-all duration-300"
              >
                Почати оптимізацію CV
              </button>
            </div>
          </div>
        </section>
        */}

        {/* ═══════════════════════════════════════════════════════════════════
            BLOCK 7: Testimonials
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="py-10 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="max-w-2xl mx-auto text-center mb-16">
              <FadeIn>
                <h2 className="text-4xl lg:text-5xl -tracking-[1.9px] mb-4 font-medium text-gray-900">
                  Ось що кажуть, про наш сервіс ті, хто вже отримав свій офер
                </h2>
              </FadeIn>
              <FadeIn delay={0.1}>
                <p className="text-lg text-gray-500">
                  Реальні історії українських IT-спеціалістів
                </p>
              </FadeIn>
            </div>

            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <FadeIn key={i} className="bg-white p-6 rounded-xl shadow-feature-card flex flex-col justify-between h-full">
                  <div>
                    <div className="flex gap-1 mb-3">
                      {[...Array(5)].map((_, si) => (
                        <StarIcon key={si} className="text-yellow-400" size={13} fill="currentColor" />
                      ))}
                    </div>
                    <p className="text-gray-700 text-lg">&quot;{t.quote}&quot;</p>
                  </div>
                  <div className="mt-6 flex gap-3 items-center">
                    <img className="size-11 rounded-lg" src={t.image} alt={t.name} loading="lazy" width={44} height={44} />
                    <div>
                      <p className="text-gray-900 text-base font-medium">{t.name}</p>
                      <span className="text-sm text-gray-500">{t.role}, {t.location}</span>
                    </div>
                  </div>
                </FadeIn>
              ))}
            </StaggerContainer>

            <div className="text-center mt-12">
              <button
                onClick={() => handleCTA("testimonials")}
                className="cursor-pointer rounded-full font-bold text-base py-3.5 px-8 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white hover:from-indigo-400 hover:to-indigo-500 hover:shadow-lg hover:shadow-indigo-500/30 transition-all duration-300"
              >
                Створити своє CV
              </button>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            BLOCK 8: Pricing
        ═══════════════════════════════════════════════════════════════════ */}
        <section id="pricing" className="bg-gray-50 py-28">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="max-w-2xl mx-auto text-center mb-16">
              <FadeIn>
                <h2 className="text-4xl lg:text-5xl -tracking-[1.9px] mb-4 font-medium text-gray-900">
                  Тарифні плани
                </h2>
              </FadeIn>
              <FadeIn delay={0.1}>
                <p className="text-lg text-gray-500">
                  Щомісячна підписка. Скасуй будь-коли. Доступ до вакансій за слотами підписки або одноразова оплата €0.99 за вакансію.
                </p>
              </FadeIn>
            </div>

            <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {plans.map((plan, index) => (
                <FadeIn
                  key={index}
                  className={`bg-white p-6 rounded-2xl flex flex-col h-full relative ${
                    plan.highlighted ? "ring-2 ring-indigo-500 shadow-lg shadow-indigo-500/10" : "shadow-feature-card"
                  }`}
                >
                  {plan.badge && (
                    <span className="inline-flex absolute top-5 right-5 text-xs bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 text-indigo-700 items-center h-6 rounded-full px-2.5 py-1 font-medium">
                      {plan.badge}
                    </span>
                  )}
                  <div>
                    <span className="inline-flex items-center h-7 rounded-full px-3 py-1 bg-gray-100 text-gray-800 text-sm font-medium mb-4">
                      {plan.name}
                    </span>
                    <p className="text-base text-gray-600 mb-6">{plan.description}</p>
                    <div className="mb-3">
                      <span className="text-5xl font-bold text-gray-900">{plan.price}</span>
                      {plan.period && (
                        <span className="text-base text-gray-500 ml-1">{plan.period}</span>
                      )}
                    </div>
                    <ul className="py-6 border-t border-dashed border-gray-200 space-y-3">
                      {plan.features.map((feature, fIndex) => {
                        const isUnavailable = typeof feature === "object" && feature.unavailable;
                        const text = typeof feature === "object" ? feature.text : feature;
                        return (
                          <li key={fIndex} className={`flex gap-2.5 items-start text-base ${isUnavailable ? "text-gray-400" : "text-gray-700"}`}>
                            {isUnavailable ? (
                              <span className="w-5 h-5 rounded-full bg-red-100 inline-flex items-center justify-center shrink-0 mt-0.5">
                                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                                  <path d="M2 2l6 6M8 2l-6 6" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </span>
                            ) : (
                              <span className="w-5 h-5 rounded-full bg-green-500 inline-flex items-center justify-center shrink-0 mt-0.5">
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 10 10" fill="none">
                                  <path d="M8.37605 2.7251L3.82605 7.2751L1.62396 5.07304" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </span>
                            )}
                            <span>{text}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  <div className="mt-auto pt-4">
                    <button
                      onClick={() => handleCTA(`pricing-${plan.name.toLowerCase()}`)}
                      className={`w-full cursor-pointer rounded-full font-bold text-base py-3.5 px-6 transition-all duration-300 ${
                        plan.highlighted
                          ? "bg-gradient-to-r from-indigo-500 to-indigo-600 text-white hover:from-indigo-400 hover:to-indigo-500 hover:shadow-lg hover:shadow-indigo-500/30"
                          : "bg-gray-900 text-white hover:bg-gray-800 hover:shadow-lg"
                      }`}
                    >
                      {plan.cta}
                    </button>
                  </div>
                </FadeIn>
              ))}
            </StaggerContainer>

            <p className="text-sm text-gray-500 text-center mt-6">
              Або відкрий окрему вакансію за €0.99 - без підписки
            </p>
            <p className="text-xs text-gray-400 text-center mt-2">
              Ціни вказані в євро. Оплата у гривні за курсом вашого банку.
            </p>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            BLOCK 9: FAQ + CTA
        ═══════════════════════════════════════════════════════════════════ */}
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        </Helmet>

        <section id="faq" className="py-10 lg:py-28">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col gap-10 lg:flex-row justify-between">
              {/* Left Column */}
              <FadeIn className="lg:w-5/12">
                <h2 className="text-4xl lg:text-5xl mb-10 lg:mb-20 -tracking-[1.9px] font-medium text-gray-900">
                  Часті запитання
                </h2>

                <div className="p-5 sm:max-w-xs rounded-2xl bg-gray-50">
                  <div className="flex mb-2 -space-x-3 items-center">
                    <img src="/images/users/d-long.webp" className="size-9 rounded-full ring-2 ring-white" alt="User 1" loading="lazy" />
                    <img src="/images/users/j-sf-c.webp" className="size-9 rounded-full ring-2 ring-white" alt="User 2" loading="lazy" />
                    <img src="/images/users/j-cheng-c.webp" className="size-9 rounded-full ring-2 ring-white" alt="User 3" loading="lazy" />
                  </div>
                  <p className="mb-6 text-gray-700">
                    Є ще запитання? Звертайся до нашої дружньої команди підтримки
                  </p>
                  <a
                    href="/contact"
                    className="py-3 px-6 rounded-full font-bold text-base bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-400 hover:to-emerald-500 hover:shadow-lg hover:shadow-green-500/30 transition-all duration-300 inline-block"
                  >
                    Зв&#39;язатися
                  </a>
                </div>
              </FadeIn>

              {/* Right Column - FAQ Items */}
              <FadeIn delay={0.2} className="lg:w-6/12">
                <div>
                  {faqItems.map((item) => (
                    <div key={item.id} className="py-6 border-b border-gray-200">
                      <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleItem(item.id)}>
                        <h3 className="text-lg font-medium text-gray-900 -tracking-[0.18px]">{item.question}</h3>
                        <button className="text-gray-950 shrink-0 ml-4 cursor-pointer" aria-label={openItem === item.id ? "Згорнути відповідь" : "Розгорнути відповідь"}>
                          {openItem === item.id ? <MinusIcon /> : <PlusIcon />}
                        </button>
                      </div>
                      <AnimatePresence initial={false}>
                        {openItem === item.id && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            style={{ overflow: "hidden" }}
                          >
                            <p className="mt-4 text-gray-700">{item.answer}</p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section
          className="pt-28 pb-10 bg-cover bg-center bg-no-repeat relative"
          style={{
            background: "url('/images/covers/hero-background.webp') no-repeat center center / cover",
          }}
        >
          <div className="bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,#fff_100%)] absolute w-full h-25 bottom-0 z-20"></div>
          <div className="max-w-7xl mx-auto px-6 relative z-30">
            <ScaleIn className="max-w-2xl mx-auto text-center">
              <h2 className="text-4xl lg:text-5xl -tracking-[1.9px] mb-4 font-medium text-gray-900">
                Готовий отримати запрошення на співбесіду?
              </h2>
              <p className="text-base text-gray-500 mb-9">
                Приєднайся до сотень IT-фахівців, які вже змінили свій підхід до пошуку роботи
              </p>
              <div className="flex gap-4 justify-center sm:items-center flex-col sm:flex-row">
                <button
                  onClick={() => handleCTA("cta-bottom")}
                  className="cursor-pointer rounded-full font-bold text-base py-3.5 px-8 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white hover:from-indigo-400 hover:to-indigo-500 hover:shadow-lg hover:shadow-indigo-500/30 transition-all duration-300"
                >
                  Створи перше CV безкоштовно
                </button>
              </div>
              <p className="text-gray-500 text-sm mt-3">
                Безкоштовний тариф - без додавання картки
              </p>
            </ScaleIn>
          </div>
        </section>
      </main>

      <FooterUA />
    </>
  );
}
